/**
 * The transport pill's readout (docs/spec/srvr/editor-shell.md SRVR-0185 AC6–AC7): the current time to
 * the millisecond, and the current beat with its fraction written over the current step's denominator,
 * so a mapper reads the beat in the same units they are stepping in. Pure, so it is unit-tested.
 */

/** `m:ss.mmm` — e.g. 62.3456 s → `1:02.346`. */
export function formatClock(seconds: number): string {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const m = Math.floor(totalMs / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

/** The smallest denominator that writes `step` as a whole fraction: 1/4 → 4, 1/48 → 48, 2/3 → 3, 1 → 1. */
export function stepDenominator(step: number): number {
  for (let d = 1; d <= 192; d++) {
    if (Math.abs(step * d - Math.round(step * d)) < 1e-6) return d;
  }
  return 1;
}

/** `ticks` sixty-fourths and the like, written as a whole beat plus a fraction over `d`, unreduced. */
function writeTicks(ticks: number, d: number): string {
  const whole = Math.floor(ticks / d);
  const num = ticks % d;
  if (num === 0) return String(whole);
  return whole === 0 ? `${num}/${d}` : `${whole} ${num}/${d}`;
}

/**
 * The beat as a whole beat plus the nearest multiple of 1/denominator, unreduced — at a 1/8 step,
 * 12.5 is `12 4/8`, not `12 1/2`. A whole beat is just the number; under one beat it is just the
 * fraction. Between steps (during playback) it is the nearest one.
 */
export function formatBeatOnStep(beat: number, step: number): string {
  const d = stepDenominator(step);
  return writeTicks(Math.max(0, Math.round(beat * d)), d);
}

/**
 * The beat the playhead is really on. A beat on the step's grid is written over the step's
 * denominator, as `formatBeatOnStep` does. A beat between the step's lines — the playhead stays where
 * it is when the step is made larger — is written exactly, over the smallest denominator that holds it
 * (6.375 is `6 3/8` at any step), and as a decimal when no denominator up to 192 does. It is never
 * rounded to the step, so a beat between lines reads the same at every step; a beat that a step change
 * moves onto or off the grid is written over that grid's denominator or the smallest that holds it. While playing it follows the
 * step, since the playhead is between lines all the time.
 */
export function formatBeatReadout(beat: number, step: number, playing: boolean): string {
  if (playing) return formatBeatOnStep(beat, step);
  const b = Math.max(0, beat);
  const d = stepDenominator(step);
  if (Math.abs(b * d - Math.round(b * d)) < 1e-6) return writeTicks(Math.round(b * d), d);
  for (let e = 1; e <= 192; e++) {
    if (Math.abs(b * e - Math.round(b * e)) < 1e-6) return writeTicks(Math.round(b * e), e);
  }
  return String(Math.round(b * 1000) / 1000);
}
